import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useDataRefresh, DEFAULT_POLL_INTERVAL_MS } from '@/hooks/useDataRefresh';
import { Plus, Search, Settings, Calendar, Gauge, AlertTriangle, Wrench } from 'lucide-react';
import { MachineFormDialog } from '@/components/Machines/MachineFormDialog';
import { MachineStopDialog } from '@/components/Machines/MachineStopDialog';
import { OEEDashboard } from '@/components/Machines/OEEDashboard';
import { useActiveStops } from '@/hooks/useMachineOEE';
import type { Machine } from '@/types/legacy';
import { MaintenanceCalendar } from '@/components/PlotterMaintenance';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

export default function Machines() {
  const { userRole } = useAuth();
  const { toast } = useToast();
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);

  const fetchMachines = async () => {
    try {
      const { data, error } = await api
        .from('machines')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMachines(data || []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las máquinas',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMachines();
  }, []);
  useDataRefresh(fetchMachines, { pollIntervalMs: DEFAULT_POLL_INTERVAL_MS });

  const filteredMachines = machines.filter(machine =>
    machine.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    machine.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    machine.type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEdit = (machine: Machine) => {
    setSelectedMachine(machine);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setSelectedMachine(null);
    setDialogOpen(true);
  };

  const canManage = userRole === 'admin' || userRole === 'supervisor';

  // For stop dialog
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [stopDialogMode, setStopDialogMode] = useState<'register' | 'resolve'>('register');
  const [selectedMachineForStop, setSelectedMachineForStop] = useState<Machine | null>(null);
  const { data: activeStops } = useActiveStops();

  const handleRegisterStop = (machine: Machine) => {
    setSelectedMachineForStop(machine);
    setStopDialogMode('register');
    setStopDialogOpen(true);
  };

  const hasActiveStop = (machineId: string) => {
    return activeStops?.some(stop => stop.machine_id === machineId);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
              Gestión de Máquinas
            </h1>
            <p className="text-muted-foreground mt-2">
              Administración de maquinaria y OEE
            </p>
          </div>
          {canManage && (
            <Button onClick={handleNew} className="bg-gradient-magma hover:shadow-magma-hover">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Máquina
            </Button>
          )}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="machines" className="w-full">
          <TabsList>
            <TabsTrigger value="machines" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Máquinas
            </TabsTrigger>
            <TabsTrigger value="maintenance" className="flex items-center gap-2">
              <Wrench className="w-4 h-4" />
              Mant. Plotter
            </TabsTrigger>
            <TabsTrigger value="oee" className="flex items-center gap-2">
              <Gauge className="w-4 h-4" />
              OEE
              {activeStops && activeStops.length > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {activeStops.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="maintenance" className="mt-6">
            <MaintenanceCalendar />
          </TabsContent>

          <TabsContent value="oee" className="mt-6">
            <OEEDashboard />
          </TabsContent>

          <TabsContent value="machines" className="mt-6 space-y-6">

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Buscar máquinas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Máquinas</p>
                <p className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
                  {machines.length}
                </p>
              </div>
              <Settings className="w-8 h-8 text-primary/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Activas</p>
                <p className="text-3xl font-bold text-success">
                  {machines.filter(m => m.is_active).length}
                </p>
              </div>
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Tipos Diferentes</p>
                <p className="text-3xl font-bold text-primary">
                  {new Set(machines.map(m => m.type).filter(Boolean)).size}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Machines Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : filteredMachines.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <Settings className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">
              {searchTerm ? 'No se encontraron máquinas' : 'No hay máquinas registradas'}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMachines.map((machine) => (
              <Card key={machine.id} className="glass-card p-6 hover:shadow-magma transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-1">
                      {machine.name}
                    </h3>
                    {machine.model && (
                      <p className="text-sm text-muted-foreground">
                        Modelo: {machine.model}
                      </p>
                    )}
                    {machine.type && (
                      <Badge variant="outline" className="mt-2">
                        {machine.type}
                      </Badge>
                    )}
                  </div>
                  <Badge variant={machine.is_active ? 'default' : 'secondary'}>
                    {machine.is_active ? 'Activa' : 'Inactiva'}
                  </Badge>
                </div>

                {machine.description && (
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {machine.description}
                  </p>
                )}

                {machine.maintenance_schedule && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                    <Calendar className="w-4 h-4" />
                    <span>{machine.maintenance_schedule}</span>
                  </div>
                )}

                {machine.last_maintenance && (
                  <p className="text-xs text-muted-foreground mb-4">
                    Último mantenimiento: {formatDistanceToNow(new Date(machine.last_maintenance), { addSuffix: true, locale: es })}
                  </p>
                )}

                {canManage && (
                  <div className="mt-4 pt-4 border-t border-border/50 space-y-2">
                    <Button
                      variant="default"
                      size="sm"
                      className="w-full"
                      onClick={() => handleEdit(machine)}
                    >
                      Editar
                    </Button>
                    {!hasActiveStop(machine.id) ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-yellow-600 border-yellow-600 hover:bg-yellow-50"
                        onClick={() => handleRegisterStop(machine)}
                      >
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        Registrar Parada
                      </Button>
                    ) : (
                      <Badge variant="destructive" className="w-full justify-center py-1">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Parada activa
                      </Badge>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
          </TabsContent>
        </Tabs>
      </div>

      <MachineFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        machine={selectedMachine}
      />

      {selectedMachineForStop && (
        <MachineStopDialog
          open={stopDialogOpen}
          onOpenChange={setStopDialogOpen}
          machineId={selectedMachineForStop.id}
          machineName={selectedMachineForStop.name}
          mode={stopDialogMode}
        />
      )}
    </DashboardLayout>
  );
}
